/**
 * mastering-chain.js — the music-mastering render pipeline (window.MasteringChain)
 *
 * 100% Web Audio: OfflineAudioContext render through BiquadFilter tone-shaping,
 * the reused `compressor-processor` glue compressor, a JS mid/side width stage,
 * LUFS-targeted loudness normalization (via LoudnessR128), and the new
 * `masterlimiter-processor` true-peak brickwall. NO FFmpeg on the critical path
 * — that is the root-cause fix for acx-mastering's #1 error (wasm_init_failed on
 * SIMD-less / Yandex / low-end devices). WAV export is pure JS (AudioPost).
 *
 * Signal flow (stereo-preserving):
 *   decode(src) → [reference FIR already applied upstream] → HPF 20Hz (DC)
 *   → tone EQ (style) → glue comp (style) → M/S width (style)
 *   → loudness push toward target LUFS → true-peak limiter (ceiling)
 *   → measure before/after (integrated LUFS, true-peak, LRA, crest).
 *
 * Public API:
 *   await window.MasteringChain.master(sourceBuffer, {
 *     targetLufs, ceilingDb, style, width, workletBase, slug, onStage,
 *     reference | referenceGenre | referenceSpec,   // reference matching:
 *        an AudioBuffer, a profile, or a list of profiles (profileOf)
 *     matchAmount                                    // 0..1, default 1
 *   }) → { buffer, before, after, appliedGainDb, maxGrDb, iterations }
 *   window.MasteringChain.TARGETS / .STYLES  (preset tables for the UI)
 */
(function () {
  'use strict';

  // Platform loudness targets (integrated LUFS, true-peak ceiling dBTP) — 2026.
  var TARGETS = {
    streaming: { lufs: -14, ceiling: -1.0 },   // Spotify / YouTube / Tidal / SoundCloud
    apple:     { lufs: -16, ceiling: -1.0 },   // Apple Music / Sound Check
    amazon:    { lufs: -14, ceiling: -2.0 },   // extra ISP headroom for Echo/Alexa
    club:      { lufs: -9,  ceiling: -1.0 },   // DJ / PA — loud, controlled
    podcast:   { lufs: -16, ceiling: -1.5 },   // spoken-word / podcast beds
    cd:        { lufs: -9,  ceiling: -0.3 },    // Red Book delivery
    // EBU R128 — European TV/radio delivery, and what many video/audiobook specs
    // demand. Asked for by a user 2026-07-28 («-23 lufs при -1Дб True Peak»);
    // the engine already hit it exactly, only the preset was missing.
    broadcast: { lufs: -23, ceiling: -1.0 }
  };

  // Intent-based tonal/dynamics/width shaping. EQ in dB; comp is gentle "glue"
  // (mastering GR is 1–3 dB, not heavy). width>1 widens (band-limited, lows kept
  // mono). Loudness is NOT set here — the normalize+limiter stages hit the target.
  var STYLES = {
    transparent: { eq: {},                                   comp: { ratio: 1.6, thr: 6,  atk: 30, rel: 160 }, width: 1.0 },
    warm:        { eq: { lowShelf: [110, 1.5], highShelf: [9000, -1.0] }, comp: { ratio: 1.8, thr: 6, atk: 30, rel: 180 }, width: 1.0 },
    punchy:      { eq: { lowShelf: [80, 1.0],  highShelf: [10000, 1.5] }, comp: { ratio: 2.2, thr: 5, atk: 15, rel: 120 }, width: 1.05 },
    clear:       { eq: { highShelf: [8000, 2.0], presence: [4000, 1.0, 1.2] }, comp: { ratio: 1.6, thr: 6, atk: 25, rel: 150 }, width: 1.05 },
    open:        { eq: { highShelf: [9000, 1.0] },           comp: { ratio: 1.5, thr: 6,  atk: 30, rel: 160 }, width: 1.15 },
    loud:        { eq: { lowShelf: [90, 0.8],  highShelf: [11000, 1.0] }, comp: { ratio: 2.6, thr: 4, atk: 10, rel: 100 }, width: 1.0 },
    // AI-music (Suno/Udio) cleanup: cut sub rumble, tame ~3–4 kHz harshness/air fizz.
    aimusic:     { eq: { hpf: 30, harsh: [3400, -1.5, 1.4], highShelf: [7500, -1.0] }, comp: { ratio: 1.8, thr: 6, atk: 25, rel: 150 }, width: 1.0 }
  };

  function AC() { return window.OfflineAudioContext || window.webkitOfflineAudioContext; }

  // Yield a macrotask so long synchronous DSP (LUFS metering, etc.) doesn't
  // freeze the main thread on multi-minute tracks.
  function yieldTask() { return new Promise(function (r) { setTimeout(r, 0); }); }

  function makeBuffer(nCh, len, sr) {
    try { return new AudioBuffer({ numberOfChannels: nCh, length: len, sampleRate: sr }); }
    catch (e) {
      var Off = AC(); var c = new Off(nCh, len, sr); return c.createBuffer(nCh, len, sr);
    }
  }

  // RBJ 2nd-order high-pass biquad, applied in place to one Float32Array.
  function hpfInPlace(data, fc, sr) {
    var w0 = 2 * Math.PI * fc / sr, cw = Math.cos(w0), sw = Math.sin(w0);
    var Q = 0.7071, alpha = sw / (2 * Q);
    var b0 = (1 + cw) / 2, b1 = -(1 + cw), b2 = (1 + cw) / 2;
    var a0 = 1 + alpha, a1 = -2 * cw, a2 = 1 - alpha;
    b0 /= a0; b1 /= a0; b2 /= a0; a1 /= a0; a2 /= a0;
    var x1 = 0, x2 = 0, y1 = 0, y2 = 0;
    for (var i = 0; i < data.length; i++) {
      var x = data[i];
      var y = b0 * x + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
      x2 = x1; x1 = x; y2 = y1; y1 = y;
      data[i] = y;
    }
  }

  // Band-limited mid/side widening. Keeps low frequencies (<~120 Hz) at their
  // original width for mono/vinyl/club translation; widens only the highs.
  // w=1 → no-op. Re-check true-peak downstream (widening can raise it).
  // RBJ biquad, in place on one array — same coefficients BiquadFilterNode uses,
  // so the width curve fitted by MasteringReference is realised exactly.
  function biquadInPlace(data, type, f0, gainDb, q, sr) {
    var w0 = 2 * Math.PI * f0 / sr, cw = Math.cos(w0), sw = Math.sin(w0);
    var A = Math.pow(10, gainDb / 40), b0, b1, b2, a0, a1, a2, al;
    if (type === 'peaking') {
      al = sw / (2 * q);
      b0 = 1 + al * A; b1 = -2 * cw; b2 = 1 - al * A;
      a0 = 1 + al / A; a1 = -2 * cw; a2 = 1 - al / A;
    } else {
      al = sw / 2 * Math.SQRT2;
      var sA = 2 * Math.sqrt(A) * al;
      if (type === 'lowshelf') {
        b0 = A * ((A + 1) - (A - 1) * cw + sA); b1 = 2 * A * ((A - 1) - (A + 1) * cw); b2 = A * ((A + 1) - (A - 1) * cw - sA);
        a0 = (A + 1) + (A - 1) * cw + sA; a1 = -2 * ((A - 1) + (A + 1) * cw); a2 = (A + 1) + (A - 1) * cw - sA;
      } else {
        b0 = A * ((A + 1) + (A - 1) * cw + sA); b1 = -2 * A * ((A - 1) + (A + 1) * cw); b2 = A * ((A + 1) + (A - 1) * cw - sA);
        a0 = (A + 1) - (A - 1) * cw + sA; a1 = 2 * ((A - 1) - (A + 1) * cw); a2 = (A + 1) - (A - 1) * cw - sA;
      }
    }
    b0 /= a0; b1 /= a0; b2 /= a0; a1 /= a0; a2 /= a0;
    var x1 = 0, x2 = 0, y1 = 0, y2 = 0;
    for (var i = 0; i < data.length; i++) {
      var x = data[i], y = b0 * x + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
      x2 = x1; x1 = x; y2 = y1; y1 = y; data[i] = y;
    }
  }

  // Frequency-dependent width: run the SIDE channel through the curve
  // MasteringReference fitted from the reference. Done with ONE temp array —
  // the same one the widener already needed — by rebuilding mid on the fly:
  //   temp = S → EQ(temp) → L,R = M ± temp → temp = HPF(temp) → L,R ±= k·temp
  // The MID channel is never touched, which is why the mono sum comes out
  // bit-for-bit unchanged (measured 0.000 dB) — the property a reference-meter
  // plugin will be checking right next to the spectrum.
  function msProcess(buf, w, sr, sideBands) {
    var hasSide = sideBands && sideBands.length;
    if (buf.numberOfChannels < 2 || (!hasSide && Math.abs(w - 1) < 1e-3)) return buf;
    if (!hasSide) return msWiden(buf, w, sr);
    var L = buf.getChannelData(0), R = buf.getChannelData(1), n = buf.length, i;
    var s = new Float32Array(n);
    for (i = 0; i < n; i++) s[i] = (L[i] - R[i]) * 0.5;
    for (i = 0; i < sideBands.length; i++) {
      if (Math.abs(sideBands[i].gainDb) < 0.3) continue;
      biquadInPlace(s, sideBands[i].type || 'peaking', sideBands[i].freq, sideBands[i].gainDb, sideBands[i].q || 1.0, sr);
    }
    for (i = 0; i < n; i++) { var m = (L[i] + R[i]) * 0.5; L[i] = m + s[i]; R[i] = m - s[i]; }
    if (Math.abs(w - 1) >= 1e-3) {                 // the style's own width, on top
      hpfInPlace(s, 120, sr);
      var k = w - 1;
      for (i = 0; i < n; i++) { var d = k * s[i]; L[i] += d; R[i] -= d; }
    }
    return buf;
  }

  function msWiden(buf, w, sr) {
    if (buf.numberOfChannels < 2 || Math.abs(w - 1) < 1e-3) return buf;
    var L = buf.getChannelData(0), R = buf.getChannelData(1), n = buf.length;
    // Algebraic shortcut — identical output, a third of the memory.
    // The textbook form builds M=(L+R)/2 and S=(L-R)/2, then writes
    // L' = M + (S + k·Shigh) and R' = M - (S + k·Shigh). But M+S ≡ L and
    // M-S ≡ R, so the whole thing collapses to L' = L + k·Shigh,
    // R' = R - k·Shigh. That drops the M and S arrays entirely: one temp
    // instead of three (146 MB less on a 6-minute stereo track — this ran on a
    // 2 GB machine in the field) and three passes instead of five.
    var side = new Float32Array(n);
    for (var i = 0; i < n; i++) side[i] = (L[i] - R[i]) * 0.5;
    hpfInPlace(side, 120, sr);                     // side content above 120 Hz
    var k = w - 1;
    for (i = 0; i < n; i++) {
      var d = k * side[i];
      L[i] += d; R[i] -= d;
    }
    return buf;
  }

  async function loadWorklet(ctx, url, slug) {
    if (window.AudioWorkletLoader && window.AudioWorkletLoader.load) {
      await window.AudioWorkletLoader.load(ctx, [url], { slug: slug });
    } else {
      await ctx.audioWorklet.addModule(url);
    }
  }

  // Stage A: HPF(DC) + reference-match EQ + tone EQ + glue compressor, offline.
  async function renderToneComp(src, style, base, slug, refBands) {
    var nCh = src.numberOfChannels, sr = src.sampleRate, len = src.length;
    var Off = AC();
    /* Рендер не двигает звук во времени (2026-10-09). Компрессор держит
       упреждение (ворклет — 3 мс, ровно lookaheadSamples; нативный запасной —
       6 мс, замер NodeLatency), насыщение — задержку передискретизации. При
       длине рендера = исходнику каждый мастер начинался с 3 мс тишины и терял
       свои последние 3 мс (замер на проде: 132 отсчёта при 44.1 кГц) — щелчок
       на стыке треков альбома. Рендер длиннее на задержку, голова отрезается.
       Длину контекста задаём до того, как станет ясно, поднимется ли ворклет,
       — поэтому по большей из двух. */
    var satAmt = (style._tone && style._tone.saturation) || 0;
    var workletLead = Math.round(sr * 0.003);
    var nativeLead = window.NodeLatency ? await window.NodeLatency.compressor(sr) : 0;
    var satLead = (satAmt > 0 && window.AudioSaturation) ? await window.AudioSaturation.latencyFrames(sr) : 0;
    var ctx = new Off(nCh, len + Math.max(workletLead, nativeLead) + satLead, sr);
    // The glue compressor prefers the high-quality AudioWorklet, but it must NOT
    // be a hard dependency: iOS Safari <14.5 has no AudioWorklet, and worklet
    // module loads can fail on flaky mobile networks. Fall back to a native
    // DynamicsCompressorNode so the master still completes everywhere.
    var haveWorklet = false;
    if (ctx.audioWorklet && typeof AudioWorkletNode !== 'undefined') {
      try { await loadWorklet(ctx, base + 'compressor-worklet.js', slug); haveWorklet = true; }
      catch (e) { haveWorklet = false; }
    }

    var srcNode = ctx.createBufferSource(); srcNode.buffer = src;
    var eq = style.eq || {};
    var chain = srcNode;
    function link(node) { chain.connect(node); chain = node; }

    // DC / sub-rumble high-pass (aimusic uses a higher corner)
    var hpfHz = eq.hpf || 20;
    var hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = hpfHz; hp.Q.value = 0.707; link(hp);

    // Тембр по ходу трека — рампа во времени (автоматизация gain). Стоит там
    // же, где в потоковом пути (mastering-stream.js buildToneChain): сразу
    // после HPF, до подгонки под эталон.
    if (style._drift && window.MasteringTonalDrift) window.MasteringTonalDrift.addToGraph(ctx, link, style._drift);

    // Точечные вырезы резонансов (авто): ДО тонального фита — грязь не
    // должна участвовать в выравнивании к норме.
    if (style._notches && style._notches.length) {
      for (var nz = 0; nz < style._notches.length; nz++) {
        var ntc = ctx.createBiquadFilter(); ntc.type = 'peaking';
        ntc.frequency.value = style._notches[nz].freq;
        ntc.gain.value = style._notches[nz].gainDb;
        ntc.Q.value = style._notches[nz].q;
        link(ntc);
      }
    }

    // Reference-match corrective EQ from MasteringReference. The gains there are
    // SOLVED for this exact cascade (overlapping filters add up in dB), so the
    // type/Q/skip rules here must stay identical to the ones it fitted against —
    // change one and the applied curve stops being the computed curve.
    if (refBands && refBands.length) {
      for (var rb = 0; rb < refBands.length; rb++) {
        if (Math.abs(refBands[rb].gainDb) < 0.3) continue;
        var rf = ctx.createBiquadFilter();
        rf.type = refBands[rb].type || 'peaking';
        rf.frequency.value = refBands[rb].freq;
        rf.gain.value = refBands[rb].gainDb;
        rf.Q.value = refBands[rb].q || 1.0;
        link(rf);
      }
    }

    // Вкусовые правки пользователя — ПОСЛЕ подгонки под эталон и рядом со
    // стилем, в той же цепочке: они не «чинят» трек, а досказывают то, что
    // человек всё равно доделал бы в своём редакторе (Pro-пользователь 3450
    // поднимал 4–6 кГц вручную почти на каждом треке — замер показал, что
    // подгонка к эталону там уже точна до 0.3 дБ, значит это вкус, а не наш
    // недочёт). Ноль = стадия не создаётся вовсе.
    var tone = style._tone || {};
    if (tone.low)      { var tl = ctx.createBiquadFilter(); tl.type = 'lowshelf';  tl.frequency.value = 100;  tl.gain.value = tone.low;      link(tl); }
    // Присутствие — ШИРОКАЯ полоса 5.5 кГц Q=0.5, а не узкий пик на 5 кГц.
    // Выбрано замером (05.08) по двум точкам, где Pro-3450 реально работает в
    // своём редакторе: +5.5 дБ на 4000 Гц и +4.4 дБ на 7365 Гц. Прежний пик
    // 5 кГц Q=1 давал на 7.4 кГц лишь 3.26 из 6 дБ — до нужного места ползунок
    // просто не дотягивался. Нынешняя форма отдаёт 5.31 и 5.33 дБ на обеих
    // точках (утечка на 1 кГц 0.71 дБ — цена ширины, для presence нормальная).
    if (tone.presence) { var tp = ctx.createBiquadFilter(); tp.type = 'peaking';   tp.frequency.value = TONE_PRESENCE_HZ; tp.gain.value = tone.presence; tp.Q.value = TONE_PRESENCE_Q; link(tp); }
    if (tone.air)      { var ta = ctx.createBiquadFilter(); ta.type = 'highshelf'; ta.frequency.value = 11000; ta.gain.value = tone.air;     link(ta); }

    if (eq.lowShelf)  { var ls = ctx.createBiquadFilter(); ls.type = 'lowshelf';  ls.frequency.value = eq.lowShelf[0];  ls.gain.value = eq.lowShelf[1];  link(ls); }
    if (eq.harsh)     { var hf = ctx.createBiquadFilter(); hf.type = 'peaking';    hf.frequency.value = eq.harsh[0];     hf.gain.value = eq.harsh[1];     hf.Q.value = eq.harsh[2] || 1.2; link(hf); }
    if (eq.presence)  { var pk = ctx.createBiquadFilter(); pk.type = 'peaking';    pk.frequency.value = eq.presence[0];  pk.gain.value = eq.presence[1];  pk.Q.value = eq.presence[2] || 1.0; link(pk); }
    if (eq.highShelf) { var hs = ctx.createBiquadFilter(); hs.type = 'highshelf'; hs.frequency.value = eq.highShelf[0]; hs.gain.value = eq.highShelf[1]; link(hs); }

    // Glue compressor. Threshold set relative to the source's RMS so GR stays
    // gentle (a few dB) regardless of input level.
    var C = style.comp || {};
    var thr = (style._rmsDb != null ? style._rmsDb : -18) + (C.thr != null ? C.thr : 6);
    var cmp;
    if (haveWorklet) {
      cmp = new AudioWorkletNode(ctx, 'compressor-processor', {
        numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [nCh],
        channelCount: nCh, channelCountMode: 'explicit',
        processorOptions: { lookaheadSamples: workletLead, detect: 'peak' }
      });
      cmp.parameters.get('threshold').value = thr;
      cmp.parameters.get('ratio').value = C.ratio || 1.6;
      cmp.parameters.get('knee').value = 8;
      cmp.parameters.get('attack').value = C.atk != null ? C.atk : 30;
      cmp.parameters.get('release').value = C.rel != null ? C.rel : 160;
      cmp.parameters.get('makeup').value = 0;
    } else {
      // Native fallback — same gentle-glue intent. DynamicsCompressorNode:
      // threshold/knee in dB, ratio, attack/release in seconds.
      cmp = ctx.createDynamicsCompressor();
      cmp.threshold.value = thr;
      cmp.knee.value = 8;
      cmp.ratio.value = C.ratio || 1.6;
      cmp.attack.value = (C.atk != null ? C.atk : 30) / 1000;
      cmp.release.value = (C.rel != null ? C.rel : 160) / 1000;
    }
    link(cmp);

    /* ── Гармоническое насыщение (exciter) ──────────────────────────────────
       Ставится ПОСЛЕ склейки и до громкостного этапа: компрессор не должен
       реагировать на гармоники, которые сам же exciter и создал, а лимитер
       обязан видеть их пики.

       Зачем оно вообще, если есть эквалайзер (замерено 2026-08-04 на записи с
       завалом верха и шипением — случай винил-рипа): в зоне выше 6 кГц
       эквалайзер +6 дБ поднял гармоники и шум ВМЕСТЕ, соотношение осталось
       1.1 дБ; насыщение подняло гармоники самого материала с −126 до −85 дБ
       при почти нетронутом шуме — 34 дБ разницы. Там, где верха физически
       нет, его нельзя усилить: можно только создать.

       ⚠️`oversample = '4x'` ОБЯЗАТЕЛЕН: на тоне 9 кГц третья гармоника уходит
       за Найквист и без передискретизации садится зеркалом в слышимую зону на
       −15.5 дБ (это брак, а не «характер»). С 4x — −107.7 дБ, то есть его нет.
       Замерено там же; менять этот флаг нельзя без повторного замера.

       Схема — параллельная: сухой сигнал целиком + верх от 3 кГц через мягкую
       кривую, подмешанный по вкусу. Так низ и середина остаются нетронутыми. */
    /* Ступень — общим примитивом (AudioSaturation, только верх от SAT_SPLIT_HZ):
       его сухой путь идёт через такой же WaveShaper ×4, что и мокрый. Своя
       сборка здесь складывала сухой сигнал с мокрым, опоздавшим на 192
       отсчёта, — гребёнка −33.6…+6.3 дБ в 3–6 кГц при насыщении 50 % (замер
       09.10); средняя энергия полос, которой мерили 04.08, её не видит. */
    if (satAmt > 0) {
      var satStage = window.AudioSaturation.buildDualBand(ctx, satAmt, { low: false, highHz: SAT_SPLIT_HZ });
      chain.connect(satStage.input); satStage.output.connect(ctx.destination);
    } else {
      chain.connect(ctx.destination);
    }
    srcNode.start(0);
    var rendered = await ctx.startRendering();
    var lead = (haveWorklet ? workletLead : nativeLead) + satLead;
    var out = makeBuffer(nCh, len, sr);
    for (var oc = 0; oc < nCh; oc++) out.copyToChannel(rendered.getChannelData(oc).subarray(lead, lead + len), oc);
    return out;
  }

  var TONE_CAP_DB = 6;
  function clampTone(v) {
    if (typeof v !== 'number' || !isFinite(v)) return 0;
    return v > TONE_CAP_DB ? TONE_CAP_DB : (v < -TONE_CAP_DB ? -TONE_CAP_DB : v);
  }

  /* ── Насыщение: одна формула на все пути ────────────────────────────────
     Держим здесь, чтобы буферный путь, потоковая дорожка и тест считали ОДНО И
     ТО ЖЕ: «насыщенность 40%» обязана звучать одинаково, откуда бы её ни
     позвали. `amount` — доля 0..1 с интерфейса. */
  /* Потолок работы лимитера. 12 дБ — замер 05.08 на живой записи (LRA 12.1):
     при срезе 9 дБ остаётся LRA 9.65, при 11.2 — 9.39, а дальше обвал: при
     15.2 дБ уже 7.82. То есть до ~12 дБ динамика держится, после — рушится. */
  var GR_SAFE_DB = 12;
  var TONE_PRESENCE_HZ = 5500, TONE_PRESENCE_Q = 0.5;   // см. комментарий у стадии
  var SAT_SPLIT_HZ = 3000;              // насыщается только верх — низ не мутнеет
  function clampSat(v) {
    if (typeof v !== 'number' || !isFinite(v)) return 0;
    return v > 1 ? 1 : (v < 0 ? 0 : v);
  }
  /* Формулы насыщения — одни на сайт: AudioSaturation (public/js/audio-saturation.js,
     туда они и были вынесены 06.08; здесь жила копия). Имена оставлены — их
     зовёт потоковая дорожка (MasteringStream.makeSaturator). */
  function satDrive(amount) { return window.AudioSaturation.drive(amount); }   // крутизна кривой
  function satMix(amount) { return window.AudioSaturation.mix(amount, 'high'); }   // сколько подмешать (насыщается только верх)
  function saturationCurve(k, n) { return window.AudioSaturation.curve(k, n); }

  /* ── Авторежим ───────────────────────────────────────────────────────────
     «Проанализировал и сам подстроил как надо» — но БЕЗ угадывания жанра и без
     вкусовых решений за пользователя. Автомат решает ровно две вещи, и обе по
     измерению:

     1. ТОН — выравнивание к BASELINE: измеренной средней тональной форме
        настоящих мастеров (та самая, из которой собраны все жанровые профили).
        Работает тот же фит, что и для эталона, поэтому у авторежима нет своей
        математики, которая могла бы разойтись с проверенной.
     2. ДИНАМИКА — сила склейки по КРЕСТ-ФАКТОРУ исходника: у зажатого материала
        склейка ослабляется, чтобы не давить дважды; у живого — усиливается,
        иначе лимитеру придётся резать пики.
        Пороги ЗАМЕРЕНЫ на стенде (04.08), а не взяты на глаз: нормальный
        материал даёт crest ≈ 15 дБ, а наш же выход, сжатый до клубной
        громкости (−7 LUFS), — 10.1 дБ. Отсюда 12 и 17: между «зажат» и
        «обычный» остаётся почти 2 дБ запаса с каждой стороны, и пограничный
        случай не переключает режим случайно. (Первая версия ставила порог 10 —
        реальный зажатый мастер с 10.1 в него НЕ попадал.)

     Ширину автомат НЕ трогает: она следует из аранжировки, а не из «нормы».
     Громкость тоже не его дело — её задаёт выбранная площадка.
     Всё, что он решил, возвращается в `auto` и показывается словами: автомат,
     который молча меняет звук, неотличим от поломки. */
  function autoDecide(before, srcCrest) {
    /* Динамику судим по ДИАПАЗОНУ ГРОМКОСТИ (LRA), а не по крест-фактору.
       Первая версия смотрела на crest — и ошибалась на самом частом материале:
       у плотного современного мастера crest 19.6 дБ (острые транзиенты), но
       LRA = 0 — он уже сплющен во времени. Авто принимало его за «живой» и
       добавляло лишние 2 дБ склейки (замер 05.08: срез 6.7 против 4.7 у
       прозрачного на стриминге и 13.0 против 11.1 на клубе). LRA описывает
       ровно то, что решает склейка: насколько громкость гуляет по треку.
       Пороги 4 и 9 LU — оттуда же: у плотного мастера LRA 0, у живой записи
       12.1; между ними обычный материал. */
    var lra = before && isFinite(before.loudnessRangeLU) ? before.loudnessRangeLU : null;
    var glue = 1.6, verdict = 'balanced';
    if (lra !== null) {
      if (lra <= 4) { glue = 1.3; verdict = 'squashed'; }
      else if (lra >= 9) { glue = 2.0; verdict = 'dynamic'; }
    } else if (isFinite(srcCrest)) {          // запасной путь: трек короче 3 с
      if (srcCrest < 12) { glue = 1.3; verdict = 'squashed'; }
      else if (srcCrest > 17) { glue = 2.0; verdict = 'dynamic'; }
    }
    return { glue: glue, verdict: verdict, crestDb: isFinite(srcCrest) ? srcCrest : null,
             lraLu: lra };
  }

  function crestFactor(detailed) {
    // crest ≈ peak - RMS (dB); higher = more dynamic/transient headroom
    if (!detailed || detailed.rmsDb == null || detailed.truePeakDb == null) return null;
    return detailed.truePeakDb - detailed.rmsDb;
  }

  async function master(src, opts) {
    opts = opts || {};
    var LR = window.LoudnessR128;
    if (!LR) throw new Error('LoudnessR128 not loaded');
    var base = opts.workletBase || '/js/';
    var slug = opts.slug || 'music-mastering';
    // Колбэки страницы — не часть DSP. Исключение из onProgress (страница
    // сбросила состояние посреди прогона, #1664: `batch.length` при
    // batch=null) не должно ронять 30-секундный рендер, а honest-отмена идёт
    // ОТДЕЛЬНЫМ каналом — opts.signal (AbortController), как у
    // MasteringStream: проверяется на каждом тике прогресса, то есть между
    // нарезанными проходами, и бросает AbortError.
    var signal = opts.signal || null;
    var stageCb = opts.onStage || null, progCb = opts.onProgress || null;
    function abortCheck() {
      if (signal && signal.aborted) { var e = new Error('MasteringChain aborted'); e.name = 'AbortError'; throw e; }
    }
    function stage(s) { abortCheck(); if (stageCb) { try { stageCb(s); } catch (_) {} } }
    // Overall 0..1 progress, weighted by the real cost of each phase (the
    // oversampled true-peak envelope dominates on long tracks).
    function prog(p) { abortCheck(); if (progCb) { try { progCb(p); } catch (_) {} } }

    var targetLufs = (typeof opts.targetLufs === 'number') ? opts.targetLufs : -14;
    var ceilingDb  = (typeof opts.ceilingDb === 'number') ? opts.ceilingDb : -1.0;
    var styleKey = opts.style || 'transparent';
    var autoMode = styleKey === 'auto';
    var style = Object.assign({}, STYLES[autoMode ? 'transparent' : styleKey] || STYLES.transparent);
    if (typeof opts.width === 'number') style.width = opts.width;
    // Вкусовые правки (дБ) едут в цепочку через объект стиля — так их видит и
    // renderToneComp, и обычный, и авто-путь, без второго канала передачи.
    if (opts.tone) {
      style._tone = {
        low: clampTone(opts.tone.low), presence: clampTone(opts.tone.presence), air: clampTone(opts.tone.air),
        saturation: clampSat(opts.tone.saturation)
      };
    }

    stage('analyze'); prog(0.02);
    await yieldTask();
    // Нарезанный измеритель: sync-вариант на плотном материале — один блок
    // до 7 с главного потока (замер 08.08, CPU 4×), числа тождественны.
    var before = await LR.measureDetailedAsync(src, { onProgress: function (p) { prog(0.02 + 0.08 * p); } });
    prog(0.10);
    style._rmsDb = isFinite(before.rmsDb) ? before.rmsDb : -18;

    // Reference-track matching (optional): derive tonal + width corrections.
    // An uploaded reference wins over a genre profile — the user's own file is
    // the more specific intent. Both feed the identical {bands, widthFactor}
    // contract, so downstream neither knows nor cares which one it got.
    var refBands = null, refSideBands = null;
    // `reference` is an AudioBuffer, a reference profile, or a list of profiles
    // (MasteringReference.profileOf) — the page keeps profiles, not audio.
    var refInput = opts.reference || opts.referenceBuffer;
    var auto = null;
    if (autoMode) {
      // Автомат решает динамику ВСЕГДА, а тон — только если человек не задал
      // цель сам: эталон (или жанр) — это уже сказанное «равняйся вот на это»,
      // и перебивать его нейтральной нормой значит спорить с пользователем.
      auto = autoDecide(before, crestFactor(before));
      style.comp = Object.assign({}, style.comp, { ratio: auto.glue });
      /* Резонансная гигиена — ТОЛЬКО в авто (его обещание — «сам решает,
         что убавить»). Октавный фит узкую грязь не берёт: замер 10.08 —
         звон +12 дБ Q9 @3.5к после авто стал ЗАМЕТНЕЕ (3.2 → 4.6 дБ).
         Детектор режет максимум 3 стационарных узких пика (звон, гул) с
         защитой от гармоник и вибрато; шесть фикстур-судей в node-стенде,
         E2E — mm-auto-notch guard. */
      if (window.ResonanceTamer && ResonanceTamer.analyzeAsync) {
        try {
          var _rn = await ResonanceTamer.analyzeAsync(src);
          if (_rn && _rn.length) style._notches = _rn;
        } catch (eRn) {}
      }
      if (!refInput && !opts.referenceGenre && !opts.referenceSpec && window.MasteringReference
          && window.MasteringReference.analyzeAuto) {
        stage('reference');
        await yieldTask();
        var autoSpec = window.MasteringReference.analyzeAuto(src);
        if (autoSpec && autoSpec.ok) {
          refBands = autoSpec.bands;
          auto.tonal = true;
          /* Насыщение автомат добавляет ТОЛЬКО там, где верха физически не
             хватает: если две верхние полосы просят больше +3 дБ, значит в
             записи нечего усиливать — эквалайзер поднимет там шум, а гармоники
             можно достроить (замер 04.08: у такой записи эквалайзер +6 дБ дал
             1.1 дБ отношения сигнал/шум, насыщение — 34 дБ). Во всех остальных
             случаях автомат насыщение НЕ трогает: на ярком материале оно даёт
             жёсткость, а вкус — не его дело. */
          var pb = autoSpec.perBandDb || [];
          if (pb.length >= 9 && !(opts.tone && opts.tone.saturation)) {
            var topLack = (pb[7] + pb[8]) / 2;
            if (topLack > 3) {
              auto.saturation = topLack > 6 ? 0.4 : 0.25;
              style._tone = Object.assign({ low: 0, presence: 0, air: 0 }, style._tone,
                { saturation: auto.saturation });
            }
          }
          /* В отчёт идёт ПРИМЕНЁННАЯ правка, а не запрошенная. Разница не
             косметическая: запрос упирается в потолок (на чистом тоне он весь
             стоял в 9 дБ), а фильтры делают меньше и мягче — рассказывать
             пользователю про 9 дБ, которых в звуке нет, значит врать числом.
             Считаем то, что реально дают наши же полосы, на наших же центрах. */
          if (window.MasteringReference.responseCurve && window.MasteringReference.CENTERS) {
            try {
              var rc = window.MasteringReference.responseCurve(autoSpec.bands, src.sampleRate,
                window.MasteringReference.CENTERS);
              auto.appliedDb = Array.prototype.slice.call(rc.db);
            } catch (e) { auto.appliedDb = null; }
          }
        }
      }
    }
    if ((refInput || opts.referenceGenre || opts.referenceSpec) && window.MasteringReference) {
      stage('reference');
      await yieldTask();
      // The page may have already analysed this pair to draw the curve — reuse
      // that instead of paying for the same FFTs twice.
      var refSpec = opts.referenceSpec || (refInput
        ? window.MasteringReference.analyze(src, refInput)
        : window.MasteringReference.analyzeProfile(src, opts.referenceGenre));
      if (refSpec && refSpec.ok) {
        if (typeof opts.matchAmount === 'number' && window.MasteringReference.withAmount) {
          refSpec = window.MasteringReference.withAmount(refSpec, opts.matchAmount);
        }
        refBands = refSpec.bands;
        // The per-frequency width curve REPLACES the single width factor when it
        // exists: it already carries both how wide and where, so multiplying the
        // two would apply the amount twice.
        if (refSpec.sideBands && refSpec.sideBands.length) refSideBands = refSpec.sideBands;
        else if (typeof refSpec.widthFactor === 'number') style.width = (style.width || 1) * refSpec.widthFactor;
      }
    }
    prog(0.16);

    /* Тембр по ходу трека (public/js/mastering-tonal-drift.js): рампа середины,
       верха и воздуха с заданного момента. Едет через объект стиля, как правка
       тона. При подгонке под эталон / тоне «Авто» рампа получает постоянное
       смещение: подгонка решена по среднему спектру трека, и без смещения верх
       второй половины срезался бы дважды (её решением и рампой). */
    var drift = (window.MasteringTonalDrift && opts.drift)
      ? window.MasteringTonalDrift.resolve(opts.drift, src.duration) : null;
    if (drift && refBands && refBands.length) drift = window.MasteringTonalDrift.withOffsets(drift, src.duration);
    style._drift = drift;

    /* Динамический EQ (public/js/mastering-dynamic-eq.js) — после тона и склейки,
       НА МЕСТЕ в bufA: буфер принадлежит цепочке, поэтому лишней копии во весь
       трек нет (память мастеринга — главный риск, см. шапку модуля). После
       насыщения: то, что насыщение досказало резкого, ловится тоже. Статика
       стиля («ИИ-музыка» −1.5 дБ на 3.4 кГц) остаётся: постоянную резкость
       правит она, всплески — этот модуль. */
    var deq = (window.MasteringDynamicEq && opts.dynEq) ? window.MasteringDynamicEq.resolve(opts.dynEq) : null;
    stage('tone');
    var bufA = await renderToneComp(src, style, base, slug, refBands);
    prog(deq ? 0.25 : 0.32);
    var dynEq = null;
    if (deq) {
      stage('dyneq');
      dynEq = await window.MasteringDynamicEq.processAsync(bufA, deq, {
        signal: signal, onProgress: function (p) { prog(0.25 + 0.07 * p); }
      });
    }
    prog(0.32);

    stage('width');
    msProcess(bufA, style.width || 1.0, src.sampleRate, refSideBands);   // in place on bufA
    prog(0.35);

    // Iterate: pre-gain toward target, brickwall-limit, measure, correct. The
    // costly oversampled true-peak envelope is computed ONCE (pre-gain only
    // scales true-peak linearly), so each correction pass is cheap.
    stage('loudness');
    var chansA = [];
    for (var ci = 0; ci < bufA.numberOfChannels; ci++) chansA.push(bufA.getChannelData(ci));
    var lufsA = await LR.measureIntegratedLUFSAsync(bufA);
    if (!isFinite(lufsA)) lufsA = targetLufs;            // silence guard
    var preGainDb = targetLufs - lufsA;
    // Tell the envelope the ceiling and the loudest gain we could reach, so it
    // can skip the exact (expensive) interpolation wherever the true peak
    // provably cannot touch the ceiling. +12 dB of headroom covers every
    // correction the loop below will make; beyond it the envelope stays SAFE
    // (conservative upper bound), never wrong.
    var envMaxGainDb = preGainDb + 12;
    var tpBase = await window.MasteringLimiter.buildEnvelopeAsync(chansA, src.sampleRate,
      { ceilLin: Math.pow(10, (ceilingDb - 0.3) / 20), maxGain: Math.pow(10, envMaxGainDb / 20) },
      function (p) { prog(0.35 + 0.37 * p); });
    await yieldTask();
    prog(0.75);
    var out = makeBuffer(bufA.numberOfChannels, bufA.length, src.sampleRate);
    // Hand these to the limiter as its destination: it writes the limited signal
    // straight into the buffer we return, instead of filling its own arrays that
    // we then copy over. One full-length buffer less alive, and one full memcpy
    // less per iteration (the loop runs up to 8 times).
    var outChans = [];
    for (var oc = 0; oc < out.numberOfChannels; oc++) outChans.push(out.getChannelData(oc));
    var scratch = {};                                   // reused across iterations

    /* The loop below is a scalar search: it is looking for ONE number (pre-gain),
       yet each probe re-limited and re-measured the ENTIRE track. On a 6-minute
       file that is ~2 s per iteration and the heaviest real-world masters burn 3-4
       of them (telemetry 2026-07-28: a Premium user's dense tracks ran 105-198 s).
       Integrated LUFS is a gated energy average, so a stratified excerpt — evenly
       spaced chunks spanning the whole track — steers the search just as well as
       the full file at a fraction of the cost. The FINAL render is still the full
       file, measured exactly, so the delivered numbers are never an estimate. */
    var probe = (function () {
      var N = chansA.length ? chansA[0].length : 0;
      var CH = Math.round(src.sampleRate * 6), PARTS = 10, total = CH * PARTS;
      if (N < total * 1.5) return null;                 // short track: no point
      var pc = [], c;
      for (c = 0; c < chansA.length; c++) pc.push(new Float32Array(total));
      var ptp = new Float32Array(total);
      for (var i = 0; i < PARTS; i++) {
        var start = Math.floor((N - CH) * i / (PARTS - 1));
        for (c = 0; c < chansA.length; c++) pc[c].set(chansA[c].subarray(start, start + CH), i * CH);
        ptp.set(tpBase.subarray(start, start + CH), i * CH);
      }
      var buf = makeBuffer(pc.length, total, src.sampleRate);
      for (c = 0; c < pc.length; c++) buf.getChannelData(c).set(pc[c]);
      var pd = [];
      for (c = 0; c < buf.numberOfChannels; c++) pd.push(buf.getChannelData(c));
      return { chans: pc, tp: ptp, buf: buf, dest: pd, scratch: {} };
    })();
    var iterations = 0, appliedGain = preGainDb, maxGr = 0, lastErr = 0;
    var prevGain = null, prevLufs = null;
    var safeGain = null, safeLufs = null, hitGrCap = false;
    for (var it = 0; it < 8; it++) {
      iterations++;
      stage('limit');
      prog(0.75 + 0.16 * Math.min(1, (it + 1) / 3));
      var lufsOut, lr;
      if (probe) {                                      // search on the excerpt
        lr = await window.MasteringLimiter.limitAsync(probe.chans, src.sampleRate,
          { ceilingDb: ceilingDb, preGainDb: preGainDb, tpBase: probe.tp, scratch: probe.scratch, dest: probe.dest });
        lufsOut = await LR.measureIntegratedLUFSAsync(probe.buf);
      } else {
        lr = await window.MasteringLimiter.limitAsync(chansA, src.sampleRate,
          { ceilingDb: ceilingDb, preGainDb: preGainDb, tpBase: tpBase, scratch: scratch, dest: outChans });
        lufsOut = await LR.measureIntegratedLUFSAsync(out);
      }
      maxGr = lr.maxGrDb;
      appliedGain = preGainDb;
      if (maxGr <= GR_SAFE_DB) { safeGain = preGainDb; safeLufs = lufsOut; }
      if (!isFinite(lufsOut)) break;
      lastErr = targetLufs - lufsOut;
      if (Math.abs(lastErr) <= 0.3) break;
      // Etalon behavior: never destroy the audio chasing a loudness number.
      // Over-compression is the field's #1 quality complaint, so once gain
      // reduction is already heavy AND we still can't get LOUD ENOUGH
      // (lastErr>0 = undershoot), stop and honestly report it. But if we
      // OVERSHOT (lastErr<0), always keep correcting down — reducing pre-gain
      // also reduces GR, so there's no reason to leave the master too loud.
      /* Раньше здесь стоял порог 14 дБ, и цикл просто ПРЕКРАЩАЛСЯ — но
         оставлял тот самый прегейн, который эти 14 дБ и вызвал. Пользователь
         получал сразу две беды (замер 05.08 на живой записи, цель −9 LUFS):
         звук раздавлен (LRA 12.1 → 6.25) И громкость всё равно не достигнута
         (−9.79). Теперь запоминаем последний БЕЗОПАСНЫЙ прегейн и, упершись,
         возвращаемся к нему: пусть тише, зато живое, и мы скажем на сколько. */
      if (maxGr >= GR_SAFE_DB && lastErr > 0.3) { hitGrCap = true; break; }
      // Secant step. Under a brickwall, +1 dB of pre-gain buys well under 1 dB
      // of loudness, and a fixed damping factor guessed that slope — which is
      // why the heaviest real-world masters burned all 8 iterations (telemetry
      // 2026-07-26: every worst case was it:8 with gr 11-14 dB, up to 85 min).
      // Two measured points give the true local slope, so this lands in 2-3.
      var step;
      if (prevGain !== null && Math.abs(lufsOut - prevLufs) > 1e-3 && Math.abs(preGainDb - prevGain) > 1e-3) {
        var slope = (lufsOut - prevLufs) / (preGainDb - prevGain);   // dLUFS per dB
        if (!isFinite(slope) || slope < 0.05) slope = 0.05;          // guard: never divide by ~0
        if (slope > 1) slope = 1;
        step = lastErr / slope;
      } else {
        step = lastErr * (maxGr > 6 ? 0.6 : 1.0);                    // first move: damped
      }
      if (step > 6) step = 6; else if (step < -6) step = -6;         // no wild jumps
      prevGain = preGainDb; prevLufs = lufsOut;
      preGainDb += step;
    }
    /* Упёрлись в потолок работы лимитера — отступаем к последнему безопасному
       прегейну. Это и есть та честность, которую цикл декларировал, но не
       исполнял: лучше отдать трек на 1–2 LU тише и живым, чем в цель по
       цифре и раздавленным. Сколько громкости уступили — скажем числом. */
    var loudnessCap = null;
    if (hitGrCap) {
      if (safeGain !== null && safeGain < appliedGain) {
        loudnessCap = { askedLufs: targetLufs, grAtAsked: maxGr };
        appliedGain = safeGain; preGainDb = safeGain;
      } else if (safeGain === null) {
        /* Потолок превышен уже на ПЕРВОЙ пробе — отступать не от чего, поэтому
           считаем отступ сами. Под брикволлом снижение прегейна на X дБ убирает
           примерно X дБ работы лимитера, а громкости отнимает заметно меньше;
           точные числа всё равно измеряются на полном рендере ниже. */
        loudnessCap = { askedLufs: targetLufs, grAtAsked: maxGr };
        appliedGain -= (maxGr - GR_SAFE_DB);
        preGainDb = appliedGain;
      }
    }

    // The search ran on an excerpt; render the real thing once, at the gain it
    // found. Everything reported downstream is measured on THIS buffer.
    if (probe) {
      // The excerpt has done its job. Let it go BEFORE the full-length render:
      // its two channels, its slice of the envelope, its AudioBuffer and its own
      // limiter scratch are ~74 MB on a 7:51 file, and they were still alive
      // through the single heaviest moment of the master — measured 2026-07-31.
      probe.chans = null; probe.tp = null; probe.buf = null; probe.dest = null; probe.scratch = null;
      stage('limit');
      var flr = await window.MasteringLimiter.limitAsync(chansA, src.sampleRate,
        { ceilingDb: ceilingDb, preGainDb: appliedGain, tpBase: tpBase, scratch: scratch, dest: outChans });
      maxGr = flr.maxGrDb;
      lastErr = targetLufs - await LR.measureIntegratedLUFSAsync(out);
      // One correction if the excerpt mis-steered us — rare, and cheap when it
      // happens because the envelope and every buffer are already in hand.
      // ⚠️ Коррекция вверх ЗАПРЕЩЕНА, если мы сознательно уступили громкость:
      // иначе она вернёт ровно тот прегейн, от которого мы только что ушли.
      if (Math.abs(lastErr) > 0.5 && maxGr < 14 && !(loudnessCap && lastErr > 0)) {
        appliedGain += lastErr;
        var clr = await window.MasteringLimiter.limitAsync(chansA, src.sampleRate,
          { ceilingDb: ceilingDb, preGainDb: appliedGain, tpBase: tpBase, scratch: scratch, dest: outChans });
        maxGr = clr.maxGrDb;
        lastErr = targetLufs - await LR.measureIntegratedLUFSAsync(out);
      }
    }
    /* ⛔ Здесь стоял «доводчик»: пока лимитер режет выше потолка — снижать ещё.
       ОТКАЗАНО ЗАМЕРОМ (05.08): приближение «−1 дБ прегейна ≈ −1 дБ среза»
       переупрощает — при живой склейке снижение гейта убирает работу лимитера
       быстрее, чем 1:1, и доводчик проскакивал мимо. В режиме «Авто» на клубной
       громкости он отдавал −14.8 LUFS вместо запрошенных −9 (уступка 5.8 LU
       вместо 2), а плотный современный мастер терял точное попадание в цель
       (−9.99 вместо −8.98) — то есть чинил редкий случай и ломал частый.
       Отступа во время поиска достаточно: он опирается на ИЗМЕРЕННЫЕ точки, а
       не на приближение. */

    var overLimited = Math.abs(lastErr) > 0.5;

    stage('measure'); prog(0.92);
    await yieldTask();
    var after = await LR.measureDetailedAsync(out, { onProgress: function (p) { prog(0.92 + 0.06 * p); } });

    // Final true-peak guarantee, verified on the OUTPUT.
    // The limiter bounds preGain·g[n]·truePeak(x)[n] ≤ ceiling, but the signal
    // it emits is x[n]·g[n] — and a fast-moving g is itself modulation, which
    // creates inter-sample peaks the input-side bound never saw. Measured
    // 2026-07-26 on a 31 dB-crest source pushed to club loudness: −0.8 dBTP
    // against a −1.0 ceiling (27 dB of gain reduction). The fixed 0.3 dB margin
    // cannot cover that, and no margin can cover every case.
    // So stop bounding and start VERIFYING: read the output's true peak with the
    // same BS.1770-4 meter we report to the user, and if it is over, trim by the
    // exact excess. A static sub-dB scalar is inaudible and turns the ceiling
    // from "bounded" into "guaranteed".
    // Verify against a slightly STRICTER target than the promised ceiling: our
    // BS.1770-4 meter reads ~0.15 dB under ffmpeg's ebur128 on heavily limited
    // material (measured 2026-07-26 — ours said -1.05, ffmpeg said -0.9 for the
    // same file). Aiming exactly at the ceiling therefore still lands over it
    // when anyone measures with a stricter meter, and the tool promises a
    // true-peak-safe master. 0.15 dB of slack is inaudible and makes the promise
    // hold under external measurement, not just our own.
    var verifyAt = ceilingDb - 0.15;
    if (isFinite(after.truePeakDb) && after.truePeakDb > verifyAt) {
      var trimDb = verifyAt - after.truePeakDb - 0.1;     // land clearly under, not on the line
      var trimLin = Math.pow(10, trimDb / 20);
      for (var tc = 0; tc < out.numberOfChannels; tc++) {
        var td = out.getChannelData(tc);
        for (var ti = 0; ti < td.length; ti++) td[ti] *= trimLin;
      }
      await yieldTask();
      after = await LR.measureDetailedAsync(out);
      lastErr = targetLufs - after.integratedLUFS;
    }
    prog(1);
    return {
      buffer: out,
      before: before,
      after: after,
      beforeCrest: crestFactor(before),
      afterCrest: crestFactor(after),
      appliedGainDb: appliedGain,
      autoNotches: style._notches || null,
      residualErrLu: lastErr,
      overLimited: overLimited,
      maxGrDb: maxGr,
      iterations: iterations,
      targetLufs: targetLufs,
      ceilingDb: ceilingDb,
      style: styleKey,
      // Уступили громкость ради динамики — с числами, а не «примерно».
      loudnessCap: loudnessCap ? {
        askedLufs: loudnessCap.askedLufs,
        deliveredLufs: after.integratedLUFS,
        givenUpLu: loudnessCap.askedLufs - after.integratedLUFS,
        grAtAsked: loudnessCap.grAtAsked, grNow: maxGr
      } : null,
      // Что автомат услышал и что решил — для честного отчёта на странице.
      auto: auto,
      tone: style._tone || null,
      // Динамический EQ: где и насколько прижал (карта, зона, отрывок «что
      // убрано»); null — выключен.
      dynEq: dynEq,
      // Тембр по ходу трека: применённая рампа (со смещением, если было); null — выключен.
      drift: style._drift || null
    };
  }

  window.MasteringChain = {
    master: master,
    // Формулы насыщения — общие для всех путей (см. комментарий у satDrive).
    satDrive: satDrive, satMix: satMix, saturationCurve: saturationCurve,
    SAT_SPLIT_HZ: SAT_SPLIT_HZ,
    TONE_PRESENCE_HZ: TONE_PRESENCE_HZ, TONE_PRESENCE_Q: TONE_PRESENCE_Q,
    msWiden: msWiden,
    msProcess: msProcess,
    TARGETS: TARGETS,
    STYLES: STYLES
  };
})();
